import React, {forwardRef, useLayoutEffect, useRef, useState} from 'react';
import {genOnKeys, onKeyHandler} from "./CommonHtml";
import {tailCheck, useRerender} from "./CommonReact";
import {icn} from "./UIIcons";
import {makeArr} from "./CommonJS";
import {genTooltip} from "../app/AppContext";

export const Button = forwardRef(({h, onClick, disabled, disabledTitle, title, className, tabIndex, onKeys, children, ...props}, ref) =>        // allow cloneElement to inject ref
    <div
        className={`flex items-center whitespace-nowrap transition-opacity [transition-property:text-shadow] ${
            tailCheck(className, "select-none", "rounded-lg",
                "outline-none focus-visible:outline-offset-[-1px] focus-visible:outline-[1px] focus-visible:outline-tx-steel",
                onClick && !disabled && "shadow-subtitle2 hover:[text-shadow:_-1px_0_0_var(--tw-shadow-color)]",
                disabled? "opacity-50 cursor-not-allowed" : onClick && !className?.includes?.("cursor-") && "cursor-pointer")}`}
        {...!disabled && {onClick: onClick || undefined, tabIndex: onClick? tabIndex || 0 : undefined,
            ...genOnKeys({Enter: onClick || undefined, " ": onClick || undefined, ...onKeys})}}
        {...genTooltip(disabled && (disabledTitle || (typeof disabled === "string" && disabled)) || title)}
        {...props} ref={ref}
    >
        {h ?? children}
    </div>
);

export function ButtonAF({autoFocus, ...props}) {
    const rEl = useRef();
    return <Button {...props} ref={!autoFocus? undefined :
        el => el && !rEl.current && setTimeout(() => el.focus(), 50) || (rEl.current = el)}/>;
}


export function useInputChange(init, overInit) {
    const vals = useRef({init: init || {}}).current;        // ensure actual original init values are used for resetInput.
    const [inp, setInp] = useState({...init || {}, ...overInit});

    return {
        inp, setInp,
        onChange: (field, value) => setInp(inp => inp[field] === value ? inp : ({...inp, [field]: value})),
        resetInput: () => (setInp({...vals.init}), {...vals.init})           // return newest values for quick use
    };
}


// text input - the component version
export const TextInput = forwardRef(({field, value, onChange, placeholder, validate, type, tag, classNameWrapper, className, addClear,
                                         btnRight, onKeys, children, ...props}, ref) => {
    const rerender = useRerender();
    const vals = useRef({}).current;
    const [isPasswordShown, setIsPasswordShown] = useState(false)
    const rBtns = useRef();

    if (vals.prevVal !== value && vals.err)
        vals.err = validate?.(value);
    vals.prevVal = value;

    useLayoutEffect(() => {
        const inp = rBtns.current?.previousSibling, wBtns = rBtns.current?.clientWidth;
        if (inp)
            inp.style.paddingRight = wBtns && ((wBtns + 10) + "px");
    }, [addClear, btnRight]);

    return (
        <div className={`relative ${classNameWrapper}`}>
            <input
                className={`block rounded-lg leading-tight appearance-none transition placeholder:italic focus-visible:-outline-offset-1 ${
                    tailCheck(className, "bg-bg1", "text-sm text-tx-cool-grey", "border focus:ring-prm-main",
                        "px-3.5 py-[0.45rem]", "w-full", vals.err ? "border-sec" : "border-tx-steel")}`}
                type={(type !== "password" || !isPasswordShown) && type || "text"}
                value={value ?? ""}
                placeholder={placeholder}
                ref={ref}
                autoComplete={props.autoComplete ?? makeArr(8).map(i => String.fromCharCode(97 + Math.random() * 26 | 0)).join('')}   // trick browser's autofill
                spellCheck={props.spellCheck ?? "false"}
                onChange={e => onChange(field, type !== "number"? e.target.value : e.target.value === "" ? "" : Number(e.target.value))}
                {...(onKeys || addClear) && genOnKeys(onKeys ?? {Escape: (e, unhandle) => !value ? unhandle() : onChange(field, "")})}
                {...props}
                {...!validate || vals.err ? {} : {onBlur: e => ((vals.err = validate?.(value)), vals.err && rerender(), props.onBlur?.(e))}}
            />
            {(addClear || btnRight || type === "password") &&
                <div ref={rBtns} className={`absolute inset-y-0 ${type === "number" ? "right-8" : "right-2"} h-full flex items-center text-title gap-0.5`}>
                    {addClear && value &&
                        <div className="cursor-pointer text-[0.8rem] p-1"
                             onClick={e => (onChange(field, ""), e.target.parentNode.previousSibling.focus())}>X</div>}
                    {btnRight}
                    {type === "password" &&
                        <Button onClick={() => setIsPasswordShown(prevVal => !prevVal)} className={isPasswordShown ? "icon-eye-blocked" : "icon-eye"}/>}
                </div>}
            {children}
            {!!vals.err && <p className="text-sec text-xs italic">{vals.err}</p>}
        </div>
    );
});

export function EditableText({normal, initText, classNameWrapper, className, onEdit}) {
    const [isEditing, setIsEditing] = useState(false);
    const [text, setText] = useState(initText);
    const onEditDone = e => (onEdit(text), setIsEditing(false));

    return (
        <div onClick={e => {
            e.stopPropagation();
            setIsEditing(true);
        }}>
            {!isEditing? normal :
                <TextInput classNameWrapper={classNameWrapper} className={className} value={text} onChange={(f,v) => setText(v)} autoFocus
                           onBlur={onEditDone} onKeys={{Enter: onEditDone}} btnRight={
                    <div className="cursor-pointer dark:text-red-500"
                         onMouseDown={e => (e.preventDefault(), e.stopPropagation())}
                         onClick={e => onEdit()}>
                        {icn.remove3}
                    </div>}
                />
            }
        </div>
    );
}

// Can work either by index or by value. governed if given optVals. onChange will be given a value instead of index.
//      optVals: value corresponding to each item in opts.
//      sel: may give current value.
//
export function Select1({field, opts, optVals, sel, idxSel, onChange, onClick, item0, placeholder, disabled, className, ...props}) {
    if (item0) opts = [item0, ...opts ?? optVals];
    return (
        <select
            id={field}
            disabled={disabled}
            value={idxSel ?? ((optVals ?? opts)?.indexOf(sel) +(item0? 1 : 0))}
            onChange={e => onChange(field, optVals? optVals[e.target.value - (item0? 1 : 0)] : (e.target.value |0))}
            className={`bg-bg1 rounded-lg leading-6 px-2.5 py-1.5 border focus-visible:-outline-offset-1 border-tx-steel ${className}`}
            {...onClick? {onClick: e => onClick(field)} : {}}
            {...props}
        >
            {(opts ?? optVals).map((opt, i) => (
                <option key={i} value={i} hidden={!!(placeholder && !i)} className={`${i || !item0 ? "" : "font-italic"} leading-10 py-1 px-2`}>{opt}</option>
            ))}
        </select>
    );
}

export const ToggleButton = ({h, field, isChecked, onChange, className, ...props}) =>
    <Button {...{h, ...props}} className={tailCheck(className, !isChecked? "opacity-50 hover:opacity-75" : "text-title")}
            onClick={e => onChange(field, !isChecked)}/>;


export const MkCheckbox = ({isChecked, className, ...props}) =>
    <div data-test="mk-checkbox" className={
        tailCheck(className, "w-5 h-5", "shrink-0 transition-opacity flex items-center justify-center",
            "border border-tx-steel bg-bg1", "rounded",
            "outline-none focus-visible:outline-offset-[0px] focus-visible:outline-[1px] focus-visible:outline-tx-steel",
            props.onClick && "cursor-pointer hover:opacity-70")}
         {...props}
    >
        {isChecked &&
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none"
                 className={className?.match?.(/(^| )w-/)? className : "w-4 h-4"}>
                <path d="M4.5 12.75l6 6 9-13.5"/>
            </svg>
        }
    </div>;

export function CheckboxRaw({field, isChecked, onChange, className, classNameSelected, ...props}) {
    const onClick = e =>  (e.stopPropagation(), onChange(field, !isChecked));
    return <MkCheckbox tabIndex={0} onClick={onChange && onClick} {...genOnKeys({" ": onClick})} {...{isChecked, ...props}}/>;
}


export function Checkbox({field, isChecked, value, isSwitch, onChange, label, className, classNameLabel, ...props}) {
    return (
        <div className={`flex items-center gap-2.5 cursor-pointer ${className}`}
             onClick={e => (e.stopPropagation(), onChange(field, !(isChecked || value)))}
             {...props}
        >
            <CheckboxRaw isChecked={isChecked || value} {...{field, onChange}}/>
            <div className={classNameLabel}>{label}</div>
        </div>
    );
}


export function Radio({field, value, isChecked, isDisabled, selected, onChange, onClick, label, col1, col2, extra, blob}) {
    const checked = !!isChecked || value === selected;
    return (
        <div className="flex items-baseline">
            <div className="flex-none w-6">
                <input type="radio" className="dark:accent-cyan-700 outline-0" name={field} checked={checked} disabled={isDisabled}
                       onChange={e => e.target.checked && onChange(field, value)}/>
            </div>
            {blob ?
                <div className="grow">{typeof blob === "function"? blob(checked) : blob}</div>
                :
                <>
                    <div className={`flex flex-none ${col1}`} onClick={e => onChange(field, value)}>
                        {extra && typeof label === "string"? label+ ":" : label}
                    </div>
                    {extra && <div className={`flex min-w-0 ${col2 || "grow"} ${!checked && "opacity-50"}`}>{extra}</div>}
                </>
            }
        </div>
    );
}

export function Radios({field, opts, selected, onChange, className, onKeys, autoFocus}) {
    return (
        <div className={`flex flex-col ${className}`} onKeyDown={onKeys && (e => onKeyHandler(e, onKeys))}>
            {opts?.map((opt, i) =>
                <label key={opt.k} onClick={e => onChange(field, opt.k)} className="flex">
                    <div className="flex-none w-6">
                        <input type="radio" className="dark:accent-cyan-700 outline-0"
                               name={field} checked={opt.k === selected} disabled={opt.isDisabled} autoFocus={!i && autoFocus}
                               onChange={e => e.target.checked && onChange(field, opt.k)}/>
                    </div>
                    {opt.h}
                </label>
            )}
        </div>
    );
}

export function FormLabel({label, classNameCol1, classNameCol2, children, className, ...props}) {
    return (
        <div className={`flex items-center ${className}`} {...props}>
            <div data-test="form-label-title" className={`flex-none ${classNameCol1}`}>{label}</div>
            <div data-test="form-label-value" className={`min-w-0 ${classNameCol2}`}>{children}</div>
        </div>
    );
}
